import React from 'react';
import { FieldSchema } from '../../types';
import { FieldWrapper } from './FieldWrapper';
import { FieldGroup } from './FieldGroup';
import { StringInput } from '../atoms/inputs/StringInput';
import { NumberInput } from '../atoms/inputs/NumberInput';
import { BooleanInput } from '../atoms/inputs/BooleanInput';
import { DateInput } from '../atoms/inputs/DateInput';

interface SchemaFieldProps {
  field: FieldSchema;
  path: string;
  value: any;
  onChange: (path: string, value: any) => void;
  errors: Record<string, string>;
  overrides: Set<string>;
  onToggleOverride: (path: string) => void;
  readOnly?: boolean;
}

interface SchemaFieldInputProps {
  field: FieldSchema;
  value: any;
  onChange: (val: any) => void;
  readOnly?: boolean;
  placeholder?: string;
  hasError: boolean;
  isOverridden: boolean;
}

const SchemaFieldInput: React.FC<SchemaFieldInputProps> = ({
  field,
  value,
  onChange,
  readOnly,
  placeholder,
  hasError,
  isOverridden
}) => {
  const commonProps = { value, onChange, readOnly, placeholder, hasError, isOverridden };

  switch (field.type) {
    case 'number':
      return <NumberInput {...commonProps} />;
    case 'boolean':
      return <BooleanInput {...commonProps} />;
    case 'date':
      return <DateInput {...commonProps} />;
    case 'email':
      return <StringInput {...commonProps} type="email" />;
    case 'string':
    default:
      return <StringInput {...commonProps} />;
  }
};

function getAllChildPaths(fields: FieldSchema[], parentPath: string): string[] {
  const paths: string[] = [];
  for (const field of fields) {
    const fieldPath = `${parentPath}.${field.key}`;
    if (field.type === 'object' && field.children) {
      paths.push(...getAllChildPaths(field.children, fieldPath));
    } else {
      paths.push(fieldPath);
    }
  }
  return paths;
}

export const SchemaField: React.FC<SchemaFieldProps> = ({
  field,
  path,
  value,
  onChange,
  errors,
  overrides,
  onToggleOverride,
  readOnly
}) => {
  if (field.type === 'object' && field.children) {
    const childPaths = getAllChildPaths(field.children, path);
    const hasErrors = childPaths.some(p => errors[p]);
    const hasOverrides = childPaths.some(p => overrides.has(p));

    return (
      <FieldGroup
        field={field}
        hasErrors={hasErrors}
        hasOverrides={hasOverrides}
      >
        {field.children.map(childField => {
          const childPath = `${path}.${childField.key}`;
          const childValue = value?.[childField.key];

          return (
            <SchemaField
              key={childField.key}
              field={childField}
              path={childPath}
              value={childValue}
              onChange={onChange}
              errors={errors}
              overrides={overrides}
              onToggleOverride={onToggleOverride}
              readOnly={readOnly}
            />
          );
        })}
      </FieldGroup>
    );
  }

  const error = errors[path];
  const isOverridden = overrides.has(path);

  return (
    <FieldWrapper
      label={field.label}
      error={error}
      isOverridden={isOverridden}
      onToggleOverride={() => onToggleOverride(path)}
      readOnly={readOnly}
      hint={field.hint}
      showIcon={field.type !== 'boolean'}
    >
      <SchemaFieldInput
        field={field}
        value={value}
        onChange={(val: any) => onChange(path, val)}
        readOnly={readOnly}
        placeholder={field.placeholder}
        hasError={!!error}
        isOverridden={isOverridden}
      />
    </FieldWrapper>
  );
};
